import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Table,
  Form,
  InputGroup,
  Badge,
  OverlayTrigger,
  Popover,
  Tooltip,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import {
  fetchallUsersByPractices,
  getUserPracticeById,
  getServicesByPracticeNumbers,
} from "../../api";
import { EditServices } from "./EditServices";
import { AddServiceInline } from "./AddServices";
import { AddCustomCodeInline } from "./AddCustomCode";
import { EditCustomCodeInline } from "./EditCustomCode";
import DeleteServiceModal from "./DeleteServiceModal";
import { restoreService } from "../../api";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import "./Services.css";

export function ViewServices({
  refresh,
  onServiceUpdate = () => {},
  addTrigger,
}) {
  const [allServices, setAllServices] = useState([]);
  const [practices, setPractices] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  // Mode toggles the entire view between regular services and custom codes.
  // Custom codes are stored in the same collection with serviceType: 'custom_code'.
  const [listMode, setListMode] = useState("service");
  // "Show deleted" toggle — when true, the list includes soft-deleted rows
  // greyed out with a Restore action in place of the standard delete button.
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const addRef = useRef(null);
  const firstTriggerSeen = useRef(true);
  const lastTriggerRef = useRef(addTrigger);
  const { toast, showToast, hideToast } = useToast();

  const isCustomCodeMode = listMode === "custom_code";

  const reload = useCallback(async () => {
    const token = sessionStorage.getItem("user");
    if (!token) return;

    const decoded = jwtDecode(token);
    const userPractices = await getUserPracticeById(decoded._id);
    setPractices(Array.isArray(userPractices) ? userPractices : []);

    const practiceNumbers = (Array.isArray(userPractices) ? userPractices : [])
      .map((p) => p.practiceNumber)
      .filter(Boolean);

    const services = await getServicesByPracticeNumbers(practiceNumbers, {
      serviceType: listMode,
      includeDeleted: showDeleted,
    });
    setAllServices(Array.isArray(services) ? services : []);

    const users = await fetchallUsersByPractices(practiceNumbers);
    setAllUsers(Array.isArray(users) ? users : []);
  }, [listMode, showDeleted]);

  useEffect(() => {
    reload();
  }, [reload, refresh]);

  useEffect(() => {
    if (firstTriggerSeen.current) {
      firstTriggerSeen.current = false;
      return;
    }
    if (lastTriggerRef.current !== addTrigger) {
      setShowAdd(true);
      setEditingServiceId(null);
      lastTriggerRef.current = addTrigger;

      setTimeout(() => {
        addRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }
  }, [addTrigger]);

  const handleEditClick = (serviceId) => {
    setShowAdd(false);
    setEditingServiceId(serviceId);
  };

  const exitEditMode = () => setEditingServiceId(null);

  const handleAdded = async () => {
    await reload();
    onServiceUpdate();
    setShowAdd(false);
  };

  const handleEdited = async () => {
    await reload();
    onServiceUpdate();
    setEditingServiceId(null);
  };

  const handleToggleMode = () => {
    // Clear transient UI state so we don't leave a service-mode add card
    // open when switching to custom codes (or vice versa).
    setShowAdd(false);
    setEditingServiceId(null);
    setSearchQuery("");
    setListMode((prev) => (prev === "service" ? "custom_code" : "service"));
  };

  const handleDeleteClick = (service) => {
    setEditingServiceId(null);
    setDeleteTarget(service);
  };

  const handleDeleted = async () => {
    await reload();
    onServiceUpdate();
  };

  const handleRestore = async (service) => {
    try {
      await restoreService(service?._id);
      const label = service?.serviceType === "custom_code" ? "Custom code" : "Service";
      showToast("success", `${label} restored successfully.`);
      await reload();
      onServiceUpdate();
    } catch (error) {
      console.error("Failed to restore service:", error);
      showToast("error", "Failed to restore. Please try again.");
    }
  };

  const filteredServices = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    // Defence in depth: even if the backend filter returns mixed types, the
    // table strictly shows only items matching the current toggle.
    return (allServices ?? [])
      .filter((s) =>
        isCustomCodeMode
          ? s?.serviceType === "custom_code"
          : s?.serviceType !== "custom_code"
      )
      .filter((s) => {
        if (!q) return true;
        return (
          (s?.servicesName ?? "").toLowerCase().includes(q) ||
          (s?.servicesDescription ?? "").toLowerCase().includes(q)
        );
      })
      .slice()
      .sort((a, b) =>
        (a?.servicesName ?? "")
          .toLowerCase()
          .localeCompare((b?.servicesName ?? "").toLowerCase())
      );
  }, [allServices, searchQuery, isCustomCodeMode]);

  const renderTariffPopover = (service) => {
    const tariffs = Array.isArray(service?.pricing?.selectedTariffs)
      ? service.pricing.selectedTariffs
      : [];
    const nappis = Array.isArray(service?.pricing?.selectedNappi)
      ? service.pricing.selectedNappi
      : Array.isArray(service?.pricing?.selectedNappis)
      ? service.pricing.selectedNappis
      : [];

    return (
      <Popover id={`tariff-popover-${service?._id}`} className="services-popover">
        <Popover.Header as="h6" style={{ background: "#CFE6FF", color: "#0F3490", fontSize: "0.75em" }}>
          Tariff & NAPPI codes
        </Popover.Header>
        <Popover.Body style={{ fontSize: "0.7em" }}>
          <div className="mb-2">
            <strong className="text-primary">Tariff codes</strong>
            {tariffs.length ? (
              <div className="ms-2">
                {tariffs.map((t, i) => {
                  const modCodes = Array.isArray(t?.modifiers)
                    ? t.modifiers.map((m) => m?.code).filter(Boolean)
                    : [];
                  const codeLabel = modCodes.length
                    ? `${t?.code} (${modCodes.join(", ")})`
                    : t?.code;
                  return (
                    <div key={`tariff-${i}`} className="small">
                      <span className="fw-semibold">{codeLabel}</span>
                      {t?.description ? ` — ${t.description}` : ""}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="ms-2 text-muted small">None</div>
            )}
          </div>
          <div>
            <strong className="text-primary">NAPPI codes</strong>
            {nappis.length ? (
              <div className="ms-2">
                {nappis.map((n, i) => (
                  <div key={`nappi-${i}`} className="small">
                    <span className="fw-semibold">{n?.nappiCode}</span>
                    {n?.name ? ` — ${n.name}` : ""}
                  </div>
                ))}
              </div>
            ) : (
              <div className="ms-2 text-muted small">None</div>
            )}
          </div>
        </Popover.Body>
      </Popover>
    );
  };

  const toggleTooltipText = isCustomCodeMode
    ? "Switch to Services"
    : "Switch to Custom Codes";
  const searchPlaceholder = isCustomCodeMode
    ? "Search custom codes by code"
    : "Search services by name or description";
  const emptyStateText = searchQuery
    ? isCustomCodeMode
      ? "No custom codes match your search."
      : "No services match your search."
    : isCustomCodeMode
      ? "No custom codes yet."
      : "No services yet.";

  return (
    <Container fluid className="bg-transparent">
      {/* Toolbar: mode toggle + search + inline add */}
      <Row className="mb-3 align-items-center g-2">
        <Col xs="auto">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="services-mode-toggle">{toggleTooltipText}</Tooltip>}
          >
            <Button
              variant="outline-primary"
              type="button"
              className="p-0 d-flex align-items-center justify-content-center"
              style={{ width: 42, height: 38 }}
              onClick={handleToggleMode}
              aria-label={toggleTooltipText}
            >
              <i
                className={
                  isCustomCodeMode
                    ? "fa-light fa-clipboard-list-check"
                    : "fa-light fa-tag"
                }
              />
            </Button>
          </OverlayTrigger>
        </Col>
        <Col xs="auto" className="px-1 d-flex align-items-center" aria-hidden="true">
          <i className="fa-light fa-arrow-left-arrow-right fs-6 text-primary" />
        </Col>
        <Col md={5} lg={4}>
          <InputGroup>
            <InputGroup.Text
              style={{
                backgroundColor: "#CFE6FF",
                border: "1px solid #CFE6FF",
                borderRight: "none",
              }}
            >
              <i className="fa-light fa-file-magnifying-glass text-primary" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ borderLeft: "none", height: "38px" }}
            />
            {searchQuery && (
              <InputGroup.Text
                onClick={() => setSearchQuery("")}
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #ced4da",
                  borderLeft: "none",
                  cursor: "pointer",
                }}
              >
                <i className="fa-solid fa-times text-muted" />
              </InputGroup.Text>
            )}
          </InputGroup>
        </Col>
        <Col xs="auto">
          <Button
            variant="primary"
            size="sm"
            type="button"
            onClick={() => {
              setEditingServiceId(null);
              setShowAdd(true);
              setTimeout(() => {
                addRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 0);
            }}
          >
            <i className="fa-solid fa-plus me-2" />
            {isCustomCodeMode ? "Add Custom Code" : "Add Service"}
          </Button>
        </Col>
        <Col xs="auto" className="ms-auto d-flex align-items-center">
          <Form.Check
            type="switch"
            id="services-show-deleted"
            label="Show deleted"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
          />
        </Col>
      </Row>

      {/* Add inline panel */}
      <div ref={addRef} />
      {showAdd && (
        <Card className="mb-3 shadow-sm">
          <Card.Header
            className="d-flex justify-content-between align-items-center"
            style={{ background: "#CFE6FF" }}
          >
            <h6 className="mb-0 fw-bold text-primary">
              {isCustomCodeMode ? "Add Custom Code" : "Add Service"}
            </h6>
            <Button
              variant="outline-secondary"
              size="sm"
              type="button"
              onClick={() => setShowAdd(false)}
            >
              Close
            </Button>
          </Card.Header>
          <Card.Body>
            {isCustomCodeMode ? (
              <AddCustomCodeInline
                onServiceUpdate={handleAdded}
                onClose={() => setShowAdd(false)}
              />
            ) : (
              <AddServiceInline
                onServiceUpdate={handleAdded}
                onClose={() => setShowAdd(false)}
              />
            )}
          </Card.Body>
        </Card>
      )}

      {/* Conditional: compact table for custom codes, full table for services */}
      {isCustomCodeMode ? (
        <Table bordered hover responsive className="fixed-table">
          <thead className="table-primary text-center align-middle">
            <tr>
              <th className="text-center align-middle text-primary" style={{ width: "55%" }}>
                Code &mdash; Invoice Description
              </th>
              <th className="text-center align-middle text-primary" style={{ width: "20%" }}>
                Tariff / NAPPI
              </th>
              <th className="text-center align-middle text-primary" style={{ width: "15%" }}>
                Flat Rate
              </th>
              <th className="text-center align-middle text-primary" style={{ width: "10%" }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredServices.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center text-muted py-4">
                  {emptyStateText}
                </td>
              </tr>
            )}
            {filteredServices.map((service) => {
              const isEditing = editingServiceId === service?._id;
              const isDeleted = !!service?.deleted;
              const ccTariffs = Array.isArray(service?.pricing?.selectedTariffs)
                ? service.pricing.selectedTariffs
                : [];
              const ccNappis = Array.isArray(service?.pricing?.selectedNappi)
                ? service.pricing.selectedNappi
                : Array.isArray(service?.pricing?.selectedNappis)
                ? service.pricing.selectedNappis
                : [];
              const invDesc = service?.pricing?.invoiceDescription || "";
              const combinedTitle = invDesc
                ? `${service?.servicesName ?? ""} — ${invDesc}`
                : (service?.servicesName ?? "");
              return (
                <React.Fragment key={service?._id}>
                  <tr className={isDeleted ? "services-row-deleted" : undefined}>
                    <td className="text-truncate" title={combinedTitle}>
                      <span className="fw-bold text-primary">
                        {service?.servicesName ?? "Untitled custom code"}
                      </span>
                      {invDesc && (
                        <span className="text-muted small">
                          {" "}&mdash; {invDesc}
                        </span>
                      )}
                    </td>
                    <td>
                      <OverlayTrigger
                        trigger={["hover", "focus"]}
                        placement="left"
                        rootClose
                        overlay={renderTariffPopover(service)}
                      >
                        <span
                          tabIndex={0}
                          style={{ cursor: "pointer" }}
                          className="text-decoration-underline text-primary small"
                        >
                          {ccTariffs.length} tariff{ccTariffs.length === 1 ? "" : "s"}
                          {" · "}
                          {ccNappis.length} NAPPI{ccNappis.length === 1 ? "" : "s"}
                        </span>
                      </OverlayTrigger>
                    </td>
                    <td>
                      {service?.pricing?.flatRate !== undefined &&
                      service?.pricing?.flatRate !== null &&
                      service?.pricing?.flatRate !== ""
                        ? service.pricing.flatRate
                        : "—"}
                    </td>
                    <td className="text-end">
                      {isEditing ? (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          type="button"
                          onClick={exitEditMode}
                          aria-label="Close editor"
                          title="Close editor"
                        >
                          Close
                        </Button>
                      ) : isDeleted ? (
                        <OverlayTrigger placement="top" overlay={<Tooltip>Restore</Tooltip>}>
                          <Button
                            variant="link"
                            type="button"
                            onClick={() => handleRestore(service)}
                            aria-label="Restore"
                            className="p-1 text-success text-decoration-none"
                          >
                            <i className="fa-solid fa-rotate-left" />
                          </Button>
                        </OverlayTrigger>
                      ) : (
                        <>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                            <Button
                              variant="link"
                              type="button"
                              onClick={() => handleEditClick(service?._id)}
                              aria-label="Edit"
                              className="p-1"
                            >
                              <i className="fas fa-edit text-primary" />
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                            <Button
                              variant="link"
                              type="button"
                              onClick={() => handleDeleteClick(service)}
                              aria-label="Delete"
                              className="p-1 text-danger text-decoration-none"
                            >
                              <i className="fa-solid fa-trash" />
                            </Button>
                          </OverlayTrigger>
                        </>
                      )}
                    </td>
                  </tr>
                  {isEditing && (
                    <tr>
                      <td colSpan={4} className="p-0">
                        <div className="service-edit-cover">
                          <EditCustomCodeInline
                            serviceId={service?._id}
                            onHide={exitEditMode}
                            onServiceUpdate={handleEdited}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <Table bordered hover responsive className="fixed-table">
          <thead className="table-primary text-center align-middle">
            <tr>
              <th className="text-center align-middle text-primary" style={{ width: "40%" }}>
                Service
              </th>
              <th className="text-center align-middle text-primary" style={{ width: "10%" }}>
                Duration
              </th>
              <th className="text-center align-middle text-primary" style={{ width: "15%" }}>
                Tariff / NAPPI
              </th>
              <th className="text-center align-middle text-primary" style={{ width: "10%" }}>
                Flat Rate
              </th>
              <th className="text-center align-middle text-primary" style={{ width: "17%" }}>
                Settings
              </th>
              <th className="text-center align-middle text-primary" style={{ width: "8%" }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredServices.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted py-4">
                  {emptyStateText}
                </td>
              </tr>
            )}

            {filteredServices.map((service) => {
              const isEditing = editingServiceId === service?._id;
              const isDeleted = !!service?.deleted;

              const tariffs = Array.isArray(service?.pricing?.selectedTariffs)
                ? service.pricing.selectedTariffs
                : [];
              const nappis = Array.isArray(service?.pricing?.selectedNappi)
                ? service.pricing.selectedNappi
                : Array.isArray(service?.pricing?.selectedNappis)
                ? service.pricing.selectedNappis
                : [];

              return (
                <React.Fragment key={service?._id}>
                  <tr className={isDeleted ? "services-row-deleted" : undefined}>
                    {service?.servicesDescription ? (
                      <OverlayTrigger
                        trigger={["hover", "focus"]}
                        placement="right"
                        rootClose
                        overlay={
                          <Popover
                            id={`desc-popover-${service?._id}`}
                            className="services-popover"
                          >
                            <Popover.Header
                              as="h6"
                              style={{ background: "#CFE6FF", color: "#0F3490", fontSize: "0.75em" }}
                            >
                              Description
                            </Popover.Header>
                            <Popover.Body style={{ fontSize: "0.7em" }}>
                              {service.servicesDescription}
                            </Popover.Body>
                          </Popover>
                        }
                      >
                        <td
                          className="services-row-cell-truncate fw-bold text-primary text-truncate"
                          style={{ cursor: "help" }}
                          tabIndex={0}
                          title={service?.servicesName ?? ""}
                        >
                          {service?.servicesName ?? "Untitled service"}
                        </td>
                      </OverlayTrigger>
                    ) : (
                      <td
                        className="services-row-cell-truncate fw-bold text-primary text-truncate"
                        title={service?.servicesName ?? ""}
                      >
                        {service?.servicesName ?? "Untitled service"}
                      </td>
                    )}
                    <td>{service?.servicesDuration ?? ""}</td>
                    <td>
                      <OverlayTrigger
                        trigger={["hover", "focus"]}
                        placement="left"
                        rootClose
                        overlay={renderTariffPopover(service)}
                      >
                        <span
                          tabIndex={0}
                          style={{ cursor: "pointer" }}
                          className="text-decoration-underline text-primary small"
                        >
                          {tariffs.length} tariff{tariffs.length === 1 ? "" : "s"}
                          {" · "}
                          {nappis.length} NAPPI{nappis.length === 1 ? "" : "s"}
                        </span>
                      </OverlayTrigger>
                    </td>
                    <td>
                      {service?.pricing?.flatRate !== undefined &&
                      service?.pricing?.flatRate !== null &&
                      service?.pricing?.flatRate !== ""
                        ? service.pricing.flatRate
                        : "—"}
                    </td>
                    <td>
                      <div className="d-flex gap-1 flex-wrap">
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`vis-${service?._id}`}>
                              Visible on website
                            </Tooltip>
                          }
                        >
                          <Badge bg={service?.settings?.visible ? "success" : "secondary"}>
                            Visible
                          </Badge>
                        </OverlayTrigger>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`book-${service?._id}`}>
                              Bookable online
                            </Tooltip>
                          }
                        >
                          <Badge bg={service?.settings?.bookable ? "success" : "secondary"}>
                            Bookable
                          </Badge>
                        </OverlayTrigger>
                      </div>
                    </td>
                    <td className="text-end">
                      {isEditing ? (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          type="button"
                          onClick={exitEditMode}
                          aria-label="Close editor"
                          title="Close editor"
                        >
                          Close
                        </Button>
                      ) : isDeleted ? (
                        <OverlayTrigger placement="top" overlay={<Tooltip>Restore</Tooltip>}>
                          <Button
                            variant="link"
                            type="button"
                            onClick={() => handleRestore(service)}
                            aria-label="Restore"
                            className="p-1 text-success text-decoration-none"
                          >
                            <i className="fa-solid fa-rotate-left" />
                          </Button>
                        </OverlayTrigger>
                      ) : (
                        <>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                            <Button
                              variant="link"
                              type="button"
                              onClick={() => handleEditClick(service?._id)}
                              aria-label="Edit"
                              className="p-1"
                            >
                              <i className="fas fa-edit text-primary" />
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                            <Button
                              variant="link"
                              type="button"
                              onClick={() => handleDeleteClick(service)}
                              aria-label="Delete"
                              className="p-1 text-danger text-decoration-none"
                            >
                              <i className="fa-solid fa-trash" />
                            </Button>
                          </OverlayTrigger>
                        </>
                      )}
                    </td>
                  </tr>

                  {isEditing && (
                    <tr>
                      <td colSpan={6} className="p-0">
                        <div className="service-edit-cover">
                          <EditServices
                            inline
                            serviceId={service?._id}
                            onHide={exitEditMode}
                            onServiceUpdate={handleEdited}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      )}

      <DeleteServiceModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        service={deleteTarget}
        onDeleted={handleDeleted}
      />
      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />
    </Container>
  );
}
